import type { App, Project } from '@hangar/shared';
import { stagger } from '@/lib/motion';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress as Bar } from '@/components/ui/progress';
import { stepNumber, type Progress, type StepState } from './steps';
import { heroView, nextTarget } from './stepViewModel';

interface Props {
  progress: Progress;
  /** Some facts are still loading: the count and the bar wait (no 4 → 7 jump while answers arrive). */
  pending: boolean;
  states: StepState[];
  projects: Project[];
  project: Project | undefined;
  apps: App[] | undefined;
  app: App | undefined;
  onProject: (slug: string) => void;
  onApp: (slug: string) => void;
}

interface ScopeSelectProps {
  label: string;
  testId: string;
  value: string;
  onChange: (value: string) => void;
  /** What the box says when there is nothing to pick ("No project yet"). */
  empty: string;
  options: Array<{ value: string; label: string }>;
}

/** "Project: StoreAI ▾": the registry Select with its label inside the trigger, so the name reads "Project: StoreAI". */
function ScopeSelect({ label, testId, value, onChange, empty, options }: ScopeSelectProps) {
  return (
    <Select value={value} onValueChange={(v) => v && onChange(v)} disabled={options.length === 0}>
      <SelectTrigger data-testid={testId} className="w-auto max-w-full justify-start gap-1.5 px-2.5 text-[13px] font-medium disabled:opacity-60 sm:text-[13px] *:data-[slot=select-value]:max-w-[14rem] *:data-[slot=select-value]:flex-1 max-sm:*:data-[slot=select-value]:max-w-none">
        <span className="shrink-0 font-mono text-[11px] font-normal text-subtle">{label}:</span>
        <SelectValue placeholder={empty} />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} data-value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/** "N / 7 steps done for": a skeleton instead of N while a fact is pending. */
function HeroCount({ done, tracked, pending }: { done: number; tracked: number; pending: boolean }) {
  return (
    <div className="flex shrink-0 items-baseline gap-2">
      <div className="tabular flex items-baseline font-mono text-[44px] font-semibold leading-none tracking-tight text-foreground" data-testid="gs-progress-count">
        {pending ? <Skeleton className="h-10 w-7 self-center" /> : done}
        <span className="text-2xl font-medium text-subtle">&nbsp;/ {tracked}</span>
      </div>
      <span className="text-[13px] text-muted-foreground">steps done for</span>
    </div>
  );
}

function ProjectSelect({ projects, project, onProject }: Pick<Props, 'projects' | 'project' | 'onProject'>) {
  return <ScopeSelect label="Project" testId="gs-project" value={project?.slug ?? ''} onChange={onProject} empty="No project yet" options={projects.map((p) => ({ value: p.slug, label: p.name }))} />;
}

function AppSelect({ apps, app, project, onApp }: Pick<Props, 'apps' | 'app' | 'project' | 'onApp'>) {
  return <ScopeSelect label="App" testId="gs-app" value={app?.slug ?? ''} onChange={onApp} empty={project ? 'No app yet' : '—'} options={(apps ?? []).map((a) => ({ value: a.slug, label: a.name }))} />;
}

/** The "Next: …" chip that jumps to the step, a skeleton while pending, or the all-done chip. */
function NextChip({ pending, next }: { pending: boolean; next: ReturnType<typeof nextTarget> }) {
  if (pending) return <Skeleton className="h-7 w-56 rounded-full" />;
  if (!next) {
    return (
      <span data-testid="gs-next" className="inline-flex w-fit items-center gap-1.5 rounded-full border border-succeeded/30 bg-succeeded/10 px-3 py-1 text-[13px] font-medium text-succeeded">
        <span aria-hidden className="size-1.5 rounded-full bg-succeeded" />
        Ready: every step is done
      </span>
    );
  }
  return (
    <a href={`#gs-step-${next.id}`} data-testid="gs-next" className="inline-flex w-fit items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[13px] font-medium text-primary transition-colors duration-150 hover:bg-primary/15">
      <span aria-hidden className="pulse size-1.5 rounded-full bg-primary" />
      Next: {next.label} <span className="font-mono text-[11px] opacity-80">· step {stepNumber(next.id)}</span>
    </a>
  );
}

function HeroBar({ view }: { view: ReturnType<typeof heroView> }) {
  return (
    <div className="flex w-full items-center gap-2.5 lg:w-64">
      <Bar value={view.barValue} aria-label={view.barLabel} className="h-2 flex-1" indicatorClassName="bg-succeeded" />
      <span className="tabular w-9 text-right font-mono text-xs text-muted-foreground">{view.percentText}</span>
    </div>
  );
}

/**
 * The one hero: N / 7 steps for the selected project and app, the two selects (URL `?project=&app=`), the green
 * progress bar and the "Next: …" chip that jumps to the step.
 */
export function SetupHero(props: Props) {
  const { progress, pending } = props;
  const view = heroView(progress, pending);
  return (
    <Card className="enter gap-5 p-5 sm:p-6" style={stagger(1)} data-testid="gs-progress" data-done={view.done} data-tracked={progress.tracked} data-next={view.next} aria-busy={pending || undefined}>
      <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4">
        <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-3">
          <HeroCount done={progress.done} tracked={progress.tracked} pending={pending} />
          <div className="flex min-w-0 flex-wrap items-center gap-2 max-sm:w-full max-sm:flex-col max-sm:items-stretch">
            <ProjectSelect projects={props.projects} project={props.project} onProject={props.onProject} />
            <span aria-hidden className="text-subtle max-sm:hidden">
              ·
            </span>
            <AppSelect apps={props.apps} app={props.app} project={props.project} onApp={props.onApp} />
          </div>
        </div>
        <div className="flex w-full flex-col gap-2.5 lg:w-auto lg:min-w-64 lg:items-end">
          <NextChip pending={pending} next={nextTarget(props.states, progress)} />
          <HeroBar view={view} />
        </div>
      </div>
    </Card>
  );
}
